import React, {useCallback, useEffect, useRef} from "react";
import axios from "axios";
import CacheService from "../../../core/services/cache/cache_service";
import Api from "../../../core/services/api/api";
import useIsMounted from "../../hooks/use-is-mounted";
import FirebaseNotificationService from "../../../core/services/firebase";
import useCachedPriceLevelsDictionaryController from "../../hooks/use-price-level-dictionary";
import useIndustry, {useCachedIndustrySchemaController, useCachedPropertyGroupsController} from "../../hooks/use-industry";
import {useHistory} from "react-router-dom";
import {LoadingView} from "../loading";
import usePartnerColorTheme from "../../hooks/use-partner-color-theme";
import {usePartnerInformationController} from "../../hooks/use-partner-information";
import {useOperatorInformationController} from "../../hooks/use-operator-information";
import {useAuthenticationState} from "../../hooks/use-auth-state";
import BroadcastService from "../../../core/services/broadcast";
import useQuery from "../../hooks/use-query";
import AuthBlockerService from "../../../core/services/auth-blocker";
import EnvService from "../../../core/services/env";
import {usePartnerConfigurationController} from "../../hooks/use-partner-configuration";

const AUTH_LOGIN_LOCK_NAME = 'operator-platform-auth-login';
const LOGIN_ATTEMPT_TIMEOUT_MS = 10000;

export interface AuthenticationLoginViewProps {
    navigateToCachedRoute: () => void;
    navigateToAccessDenied: () => void;
}

export const AuthenticationLoginView: React.FC<AuthenticationLoginViewProps> = ({navigateToCachedRoute, navigateToAccessDenied}) => {
    const history = useHistory();
    const historyRef = useRef<typeof history>(history);
    historyRef.current = history;

    const [query] = useQuery({parseNumbers: false, parseBooleans: false});
    const token = (query.token as string | undefined) ?? "";
    const time = query.time || query.tokenExpiration;
    const lockedOperatorId = query.lockedOperatorId as string | undefined;

    const {loggedIn} = useAuthenticationState();
    const {getPartnerColorTheme} = usePartnerColorTheme();
    const {getPartnerInformation} = usePartnerInformationController();
    const {getOperatorInformation} = useOperatorInformationController();
    const {fetchConfigurations} = usePartnerConfigurationController();
    const {invalidateDictionaryCache} = useCachedPriceLevelsDictionaryController();
    const {invalidateSchemaCache} = useCachedIndustrySchemaController();
    const {invalidatePropertyGroupAttributesCache, invalidatePropertyGroupsCache} = useCachedPropertyGroupsController();
    const {invalidateIndustriesCache} = useIndustry();
    const isMounted = useIsMounted();

    /**
     * Invalidates all the caches related to the dictionary, schema, property groups and industries
     * @type {(function(): void)}
     */
    const invalidateCaches = useCallback(() => {
        invalidateDictionaryCache();
        invalidateSchemaCache();
        invalidatePropertyGroupAttributesCache();
        invalidatePropertyGroupsCache();
        void invalidateIndustriesCache();
    }, [invalidateDictionaryCache, invalidateSchemaCache, invalidatePropertyGroupAttributesCache, invalidatePropertyGroupsCache, invalidateIndustriesCache]);

    /**
     * Caches the login results of the user using the CacheService.
     */
    const cacheLoginResults = useCallback((response) => {
        const {token, tokenLifeSpanMinutes, userId} = (response?.data?.loginResponse ?? {});
        CacheService.setAutoInfo(token, tokenLifeSpanMinutes);
        CacheService.setBizTaskServiceApplicationId(response.data?.bizTaskServiceApplicationId);
        CacheService.setBizNoteServiceApplicationId(response.data?.bizNoteServiceApplicationId);
        CacheService.setOperatorProfileImageBaseUrl(response.data?.profileBaseURL);
        CacheService.setOperatorId(userId);
    }, [])

    /**
     * Registers the user into the notification platform using the FirebaseNotificationService.
     *
     * first deactivates the currently saved token of the user and registers them under a new notification token.
     */
    const registerUserForNotifications = useCallback(async () => {
        await FirebaseNotificationService.deactivateDevice()
        await FirebaseNotificationService.activateDevice()
    }, []);

    /**
     * Fetches the nexus search configs from the server api.
     * @type {(function(): Promise<void>)}
     */
    const getNexusSearchConfigs = useCallback(async () => {
        const response = await Api.getNexusSearchConfigurations();
        if (response.resultFlag) {
            CacheService.setNexusSearchConfigs({
                canToggleAdvancedMode: response?.data?.advancedSearchInOperator,
                canSeeAutocompleteSuggestions: response?.data?.showSuggestionInOperator,
                sidebarFacetsCollapsedByDefault: response?.data?.defaultCollapsedInOperator,
                hidePropertyGroupFacetMostFrequentValues: response?.data?.hideGroupPropMostFrequentItemsInOperator,
            });
        }
    }, [])

    /**
     * Notifies other tabs and parent windows about the login result of the user by broadcasting a message using the BroadcastService.
     */
    const notifyLoginResult = useCallback((resultSuccessful: boolean) => {
        if (resultSuccessful) {
            if (AuthBlockerService.isInAuthIframe) {
                // The parent will then dispatch their own event.
                window.parent.postMessage({type: 'auth-login-success'}, '*');
            } else {
                // Dispatch a local storage event or broadcast channel for other tabs
                BroadcastService.postMessage(BroadcastService.channelNames.authBlockerService, {type: 'login-success'});
            }
        } else {
            if (AuthBlockerService.isInAuthIframe) {
                // The parent will then dispatch their own event.
                window.parent.postMessage({type: 'auth-login-failed'}, '*');
            } else {
                // Dispatch a local storage event or broadcast channel for other tabs
                BroadcastService.postMessage(BroadcastService.channelNames.authBlockerService, {type: 'login-failed'});
            }
        }
    }, [])

    /**
     * Logs the user into the system by calling the server api.
     *
     * if the login is successful, then:
     * 1. the login result is cached for different purposes in the application
     * 2. partner configurations are force-fetched into the shared context
     * 3. partner / operator information and color theme are loaded
     * 4. user is registered for notification platform
     * 5. user is redirected to appropriate cached route or landing view.
     */
    /**
     * Applies one settled login response while this document still holds the login lock.
     * A silent Api.login is not passed here; the caller retries that call itself.
     */
    const applyLoginResponse = useCallback(async (response) => {
        if (!response?.resultFlag) {
            if (isMounted()) {
                navigateToAccessDenied();
            }
            notifyLoginResult(false);
            return;
        }
        const userId = response.data?.loginResponse?.userId;
        if (AuthBlockerService.isInAuthIframe && lockedOperatorId && lockedOperatorId.toString() !== userId?.toString()) {
            window.parent.postMessage({type: 'auth-login-mismatch'}, '*');
            return;
        }

        cacheLoginResults(response);
        if (!isMounted()) {
            notifyLoginResult(true);
            return;
        }
        await fetchConfigurations({useCached: false});
        await getPartnerInformation({disregardCache: true});
        await getPartnerColorTheme();
        await getOperatorInformation({disregardCache: true});
        void registerUserForNotifications()
        void getNexusSearchConfigs();
        invalidateCaches();
        if (!AuthBlockerService.isInAuthIframe) {
            navigateToCachedRoute();
        }
        notifyLoginResult(response?.resultFlag ?? false);
    }, [lockedOperatorId, isMounted, navigateToAccessDenied, cacheLoginResults, fetchConfigurations, notifyLoginResult, getPartnerInformation, getPartnerColorTheme, getOperatorInformation, registerUserForNotifications, getNexusSearchConfigs, invalidateCaches, navigateToCachedRoute])

    const cancelLoginAttemptRef = useRef<(() => void) | null>(null);

    /**
     * Holds the login attempt for this document.
     * When the auth blocker is on, Api.login is cancelled and repeated after 10 seconds of silence.
     * When it is off, Api.login runs once with no lock and no timeout.
     * @param isDisposed reports whether this login view has unmounted
     * @param singleAttempt skip the Web Lock retry and call Api.login once
     */
    const runAsLoginOwner = useCallback(async (isDisposed: () => boolean, singleAttempt = false) => {
        if (!singleAttempt && CacheService.isLoggedIn()) {
            notifyLoginResult(true);
            if (!AuthBlockerService.isInAuthIframe && !isDisposed()) {
                navigateToCachedRoute();
            }
            return;
        }
        while (!isDisposed()) {
            const source = axios.CancelToken.source();
            cancelLoginAttemptRef.current = () => source.cancel('disposed');
            const timer = singleAttempt ? null : window.setTimeout(() => source.cancel('timeout'), LOGIN_ATTEMPT_TIMEOUT_MS);
            let response;
            try {
                response = await Api.login(token ?? "", source.token);
            } finally {
                if (timer != null) {
                    window.clearTimeout(timer);
                }
                if (cancelLoginAttemptRef.current) {
                    cancelLoginAttemptRef.current = null;
                }
            }
            if (isDisposed()) {
                return;
            }
            if (response?.aborted) {
                if (singleAttempt) {
                    return;
                }
                continue;
            }
            await applyLoginResponse(response);
            return;
        }
    }, [applyLoginResponse, navigateToCachedRoute, notifyLoginResult, token])

    const runAsLoginOwnerRef = useRef(runAsLoginOwner);
    runAsLoginOwnerRef.current = runAsLoginOwner;

    /**
     * On the component mount:
     * - if there is no Core token, leaves for the cached route or access denied.
     * - otherwise takes the cross-tab login lock. Only the document that is granted the lock calls Api.login.
     * Documents still queued when this view unmounts leave the queue. Closing the granted document releases the lock for exactly one successor.
     */
    useEffect(() => {
        if (!token || !time) {
            if (loggedIn) {
                return navigateToCachedRoute();
            } else {
                return navigateToAccessDenied();
            }
        }
        const abortController = new AbortController();
        let disposed = false;
        const isDisposed = () => disposed;

        const start = async () => {
            if (!EnvService.DEV_useAuthBlocker) {
                await runAsLoginOwnerRef.current(isDisposed, true);
                return;
            }
            const run = () => runAsLoginOwnerRef.current(isDisposed);
            if (typeof navigator !== 'undefined' && navigator.locks?.request) {
                try {
                    await navigator.locks.request(AUTH_LOGIN_LOCK_NAME, {signal: abortController.signal}, run);
                } catch (error) {
                    if (abortController.signal.aborted || disposed) {
                        return;
                    }
                    throw error;
                }
                return;
            }
            await run();
        };
        void start();
        return () => {
            disposed = true;
            cancelLoginAttemptRef.current?.();
            abortController.abort();
        };
        // eslint-disable-next-line
    }, []);

    return (
        <LoadingView message={"Welcome back to Operator Platform"}/>
    );
}
